import { ChangeDetectionStrategy, Component, computed, inject, input, model, ViewEncapsulation } from '@angular/core';
import { IQuizQuestion, IQuizQuestionAnswer, IQuizQuestionPresetOption } from '@fi-app/fi-onboarding/fi-quiz/fi-quiz';
import { isDefined } from '@fi-shared/utils';
import { FiQuizQuestionType } from '@fi-app/fi-onboarding/fi-quiz/fi-quiz-enums';
import { FiOnboardingStepCompletionDialogState } from '@fi-app/fi-onboarding/fi-onboarding-completion/fi-onboarding-step-completion-dialog/fi-onboarding-step-completion-dialog-state.service';

@Component({
    selector: 'fi-quiz-progress-current-question',
    templateUrl: './quiz-progress-current-question.component.html',
    styleUrl: './quiz-progress-current-question.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-quiz-progress-current-question' },
    standalone: false,
})
export class FiQuizProgressCurrentQuestionComponent {
    public question = input.required<IQuizQuestion>();
    public isQuizCompleted = input<boolean>(false);
    public answer = model.required<IQuizQuestionAnswer>();
    public isResultMode = input<boolean>(false);
    public isCorrectAnswerRevealed = input<boolean>(false);
    public isRetakeAllowed = input<boolean>(false);
    public isReportModeQuizNotCompletedOrInProgress = input<boolean>(false);

    protected readonly completionDialogState = inject(FiOnboardingStepCompletionDialogState);

    // https://chainformation.atlassian.net/browse/FI-6659?focusedCommentId=89379
    // workaround for single choice questions
    protected firstSelectedOptionValue = computed(() => {
        return this.question().PresetOptions.find((option) => this.isOptionSelected(option.Value))?.Value;
    });

    protected isMultipleChoiceQuestion = computed(() => {
        return this.question().Type === FiQuizQuestionType.MultipleAnswers;
    });

    private isNotCompletionMode = computed(() => {
        return this.isResultMode() || this.completionDialogState.isReportMode();
    });

    protected isNotAllCorrectAnswersSelectedBannerShown = computed(() => {
        return (
            !this.completionDialogState.isReportMode() &&
            this.isRetakeAllowed() &&
            this.isResultMode() &&
            this.isMultipleChoiceQuestion() &&
            this.question().PresetOptions.some((option) => this.isOptionSkipped(option)) &&
            this.isCorrectAnswerPresent() &&
            !this.isQuizCompleted()
        );
    });

    protected containsRevealedSkippedOptions = computed(() => {
        return (
            this.question().PresetOptions.some((option) => this.isOptionSkipped(option)) &&
            this.isCorrectAnswerRevealed()
        );
    });

    private readonly isCorrectAnswerPresent = computed(() => {
        const correctAnswers = this.question().PresetOptions.filter((option) => option.IsCorrect);
        return correctAnswers.some((correctAnswer) => this.answerValue().includes(correctAnswer.Value));
    });

    private answerValue = computed(() => {
        return isDefined(this.answer()?.Value) ? this.answer().Value : [];
    });

    protected isOptionSelected(optionValue: string): boolean {
        return this.answerValue().includes(optionValue);
    }

    protected isCorrectAnswerOption(option: IQuizQuestionPresetOption): boolean {
        if (this.isReportModeQuizNotCompletedOrInProgress()) {
            return false;
        }
        const isOptionAnsweredCorrectly =
            this.isNotCompletionMode() && option.IsCorrect && this.answerValue().includes(option.Value);
        if (!this.isCorrectAnswerRevealed()) {
            return isOptionAnsweredCorrectly;
        }
        return isOptionAnsweredCorrectly || this.isOptionSkipped(option);
    }

    protected isIncorrectAnswerOption(option: IQuizQuestionPresetOption): boolean {
        if (this.isReportModeQuizNotCompletedOrInProgress()) {
            return false;
        }
        return this.isNotCompletionMode() && this.answerValue().includes(option.Value) && !option.IsCorrect;
    }

    protected isOptionSkipped(option: IQuizQuestionPresetOption): boolean {
        if (this.isReportModeQuizNotCompletedOrInProgress()) {
            return false;
        }
        return this.isNotCompletionMode() && option.IsCorrect && !this.answerValue().includes(option.Value);
    }

    protected onOptionChanged(optionValue: string): void {
        if (this.isResultMode() || this.answer().IsDisabled) {
            return;
        }

        this.answer.update((answer) => {
            let answerValue: string[];
            if (this.isMultipleChoiceQuestion()) {
                answerValue = this.isOptionSelected(optionValue)
                    ? this.answerValue().filter((value) => value !== optionValue)
                    : [...this.answerValue(), optionValue];
            } else {
                answerValue = [optionValue];
            }

            return {
                ...answer,
                Value: answerValue,
            };
        });
    }

    protected onSelectedSingleOptionValueChanged(newOptionValue: string) {
        this.onOptionChanged(newOptionValue);
    }
}
